import React from "react";
import Layout from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import { useEntries } from "@/context/EntriesContext";
import {
  SESSIONS, MILESTONES, STAGES, SUCCESS_MEASURES, CHECKPOINTS,
  INTAKE_FIELDS, stageOf,
} from "@/data/bridgeData";
import { ACTIVITY_TOOLS, DELIVERABLE_DOCS, serializeTool } from "@/data/activityTools";
import { Printer, FileText } from "lucide-react";

function Block({ label, value }) {
  if (!value || !String(value).trim()) return null;
  return (
    <div className="mb-4">
      <div className="text-sm font-medium text-stone-900 mb-1">{label}</div>
      <p className="whitespace-pre-wrap text-[15px] leading-relaxed text-stone-700">{value}</p>
    </div>
  );
}

export default function ExportKit() {
  const { user, activeParticipant } = useAuth();
  const { getEntry } = useEntries();
  const isCoach = user?.role === "coach";
  const subjectName = isCoach ? activeParticipant?.name : user?.name;
  const scales = SUCCESS_MEASURES.filter((m) => m.type === "scale");
  const today = new Date().toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });

  const toolsForSession = (sid) =>
    Object.entries(ACTIVITY_TOOLS).filter(([, t]) => t.sessionId === sid).map(([id, t]) => ({ id, ...t }));

  return (
    <Layout coachContext>
      {/* Toolbar (not printed) */}
      <div className="no-print mb-8 flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="label-eyebrow mb-2">Facilitator kit</div>
          <h1 className="font-serif text-4xl font-light text-stone-900">Export the journey</h1>
          <p className="mt-2 max-w-xl text-stone-600 leading-relaxed">
            A complete, print-ready document – session agendas, completed worksheets, exercise outputs, and final deliverables.
            Use your browser's "Save as PDF" in the print dialog.
          </p>
        </div>
        <button
          data-testid="export-print-btn"
          onClick={() => window.print()}
          className="group inline-flex items-center gap-2 rounded-sm bg-brand-primary px-5 py-3 text-white transition-all duration-300 hover:bg-brand-hover"
        >
          <Printer size={17} /> Download PDF
        </button>
      </div>

      {/* Printable document */}
      <div data-testid="export-document" className="bg-white">
        {/* Cover */}
        <section className="print-section border-b border-stone-200 pb-10 mb-10">
          <div className="flex items-center gap-3 mb-8">
            <span className="block h-px w-10 bg-brand-primary" />
            <span className="font-serif text-2xl text-stone-900">Building The Bridge</span>
          </div>
          <div className="label-eyebrow mb-3">16-week facilitator kit</div>
          <h2 className="font-serif text-5xl font-light text-stone-900 leading-tight">
            {subjectName || "Participant"}'s journey
          </h2>
          <p className="mt-4 text-stone-500">Generated {today}</p>
        </section>

        {/* Intake */}
        <section className="print-section mb-12">
          <div className="label-eyebrow mb-4">Intake & transition goal</div>
          {INTAKE_FIELDS.map((f) => <Block key={f.id} label={f.label} value={getEntry(`intake:${f.id}`, "")} />)}
          {!INTAKE_FIELDS.some((f) => getEntry(`intake:${f.id}`, "")) && <p className="text-sm text-stone-400">No intake recorded.</p>}
        </section>

        {/* Journey + measures */}
        <section className="print-section mb-12">
          <div className="label-eyebrow mb-4">Journey overview</div>
          <div className="grid grid-cols-5 gap-3 mb-8">
            {STAGES.map((s, i) => (
              <div key={s.id} className="print-card rounded-sm border border-stone-200 p-3">
                <div className="text-xs text-stone-400">0{i + 1}</div>
                <div className="font-serif text-lg text-stone-900">{s.name}</div>
                <div className="text-xs text-stone-400">{s.weeks}</div>
              </div>
            ))}
          </div>
          <div className="label-eyebrow mb-3">Confidence & clarity (1–10)</div>
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="border-b border-stone-200 text-left text-stone-500">
                <th className="py-2 font-medium">Measure</th>
                {CHECKPOINTS.map((cp) => <th key={cp.id} className="py-2 font-medium text-center">{cp.label}</th>)}
              </tr>
            </thead>
            <tbody>
              {scales.map((m) => (
                <tr key={m.id} className="border-b border-stone-100">
                  <td className="py-2 text-stone-700">{m.label}</td>
                  {CHECKPOINTS.map((cp) => (
                    <td key={cp.id} className="py-2 text-center font-serif text-lg text-stone-900">{getEntry(`measure:${cp.id}:${m.id}`, 0)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        {/* Milestones */}
        <section className="print-section mb-12">
          <div className="label-eyebrow mb-4">Milestones</div>
          <div className="space-y-2">
            {MILESTONES.map((m) => (
              <div key={m.n} className="flex items-start gap-3">
                <span className="font-serif text-lg text-brand-primary w-6">{m.n}</span>
                <div>
                  <span className="text-stone-900 font-medium">{m.title}</span>
                  <span className="ml-2 text-sm text-stone-500">{getEntry(`milestone:${m.n}:done`, false) ? "· reached" : "· in progress"}</span>
                  <Block label="" value={getEntry(`milestone:${m.n}:note`, "")} />
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Sessions */}
        {SESSIONS.map((s) => {
          const stage = stageOf(s.stage);
          const tools = toolsForSession(s.id);
          const coachNote = getEntry(`coachnote:s${s.id}`, "");
          return (
            <section key={s.id} className="print-break print-section mb-12 pt-8 border-t border-stone-200">
              <div className="label-eyebrow mb-2">Session {s.id} · Week {s.week} · {stage.name}</div>
              <h3 className="font-serif text-3xl text-stone-900 mb-2">{s.title}</h3>
              <p className="text-stone-600 leading-relaxed mb-6">{s.purpose}</p>

              <div className="mb-6">
                <div className="text-sm font-medium text-stone-900 mb-2">Agenda</div>
                <ol className="space-y-1">
                  {s.structure.map((step, i) => (
                    <li key={step} className="text-[15px] text-stone-700"><span className="text-brand-primary mr-2">{i + 1}.</span>{step}</li>
                  ))}
                </ol>
              </div>

              <div className="mb-6">
                <div className="text-sm font-medium text-stone-900 mb-2">Reflections</div>
                {s.exercises.map((ex) => {
                  const resp = getEntry(`s${s.id}:ex:${ex.id}`, "");
                  const synth = getEntry(`s${s.id}:synth:${ex.id}`, "");
                  if (!resp && !synth) return <p key={ex.id} className="text-sm text-stone-400 mb-2">{ex.title}: –</p>;
                  return (
                    <div key={ex.id} className="mb-4">
                      <Block label={ex.title} value={resp} />
                      {synth && <p className="border-l-2 border-brand-sage/40 pl-3 text-[15px] italic text-stone-600 whitespace-pre-wrap">{synth}</p>}
                    </div>
                  );
                })}
              </div>

              {tools.length > 0 && (
                <div className="mb-6">
                  <div className="text-sm font-medium text-stone-900 mb-2">Exercise outputs</div>
                  {tools.map((t) => {
                    const content = serializeTool(getEntry, t.id);
                    const synth = getEntry(`tool:${t.id}:synth`, "");
                    if (!content.trim() && !synth) return null;
                    return (
                      <div key={t.id} className="mb-4">
                        <Block label={t.title} value={content} />
                        {synth && <p className="border-l-2 border-brand-sage/40 pl-3 text-[15px] italic text-stone-600 whitespace-pre-wrap">{synth}</p>}
                      </div>
                    );
                  })}
                </div>
              )}

              {coachNote && (
                <div className="print-card rounded-sm border border-stone-200 bg-brand-parchment/30 p-4">
                  <div className="label-eyebrow mb-1" style={{ color: "#8C6B5D" }}>Coach notes</div>
                  <p className="whitespace-pre-wrap text-[15px] text-stone-700">{coachNote}</p>
                </div>
              )}
            </section>
          );
        })}

        {/* Deliverables */}
        <section className="print-break print-section pt-8 border-t border-stone-200">
          <div className="flex items-center gap-2 mb-4">
            <FileText size={16} className="text-brand-primary" />
            <div className="label-eyebrow">Final deliverables</div>
          </div>
          {DELIVERABLE_DOCS.filter((d) => String(getEntry(d.key, "")).trim()).length === 0 ? (
            <p className="text-sm text-stone-400">No deliverables drafted yet.</p>
          ) : (
            DELIVERABLE_DOCS.map((d) => <Block key={d.key} label={d.title} value={getEntry(d.key, "")} />)
          )}
        </section>
      </div>
    </Layout>
  );
}
